import { Component, Input, OnDestroy, Output } from '@angular/core';
import { ReplaySubject, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { RenderingContext, RenderingContextProvider, SitePage } from './../../../api';
import {
  createSingleSubject,
  DEFAULT_FETCH_LEVELS,
  DEFAULT_LAYOUT_MODE,
  KEY_LAYOUT_MODE,
  KEY_LEVELS,
  opBoxLayoutMode,
  opLevels,
  opShareLast,
  pageDistinctUntilChanged,
  perfMeasure,
} from './../../../utils';
import { WchService } from './../../services/wch.service';
import { defineProperties } from './../../utils/js.utils';
import { AbstractLifeCycleComponent, createSetter } from './../rendering/abstract.lifecycle.component';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'PagerefComponent';

/**
 * Combines the active pieces of the state into one single object
 */
export declare type ComponentState = [RenderingContext, string];

@Component({
  selector: 'wch-pageref',
  templateUrl: './pageref.component.html',
  styleUrls: ['./pageref.component.css'],
  preserveWhitespaces: false
})
export class PagerefComponent extends AbstractLifeCycleComponent
  implements OnDestroy, RenderingContextProvider {
  /*
   * Accepts the site page
   */
  @Input() sitePage: SitePage;

  /*
   * Set the layout mode to render this page
   *
   * @param aValue the new layout mode, if nothing is given this defaults to 'default'
   */
  @Input() layoutMode: string;

  /*
   * Accepts the number of levels to be queried
   */
  @Input() levels: string | number | null | undefined;

  /*
   * This monitors the current rendering context
   */
  @Output() readonly onRenderingContext: Observable<RenderingContext>;

  /*
   * Event that propagates the newly created component
   */
  @Output() readonly onComponent: ReplaySubject<any> = createSingleSubject<any>();

  /*
   * Fires whenever the layout mode changed, includes the initial default mode.
   */
  @Output() readonly onLayoutMode: Observable<string>;

  /**
   * The component state
   */
  readonly onState: Observable<ComponentState>;

  /*
   * Initialize the component and build the streams
   *
   * @param aWchService the WCH service to get the context from
   */
  constructor(aWchService: WchService) {
    super();
    // back reference
    const that = this;

    // measure startup
    const handle = perfMeasure(LOGGER);

    // the destroy handler
    const onDestroy = <T>() => takeUntil<T>(that.onOnDestroy);

    // handle the query levels
    const levelsSubject = new BehaviorSubject<
      string | number | null | undefined
    >(DEFAULT_FETCH_LEVELS);

    const onLevels = levelsSubject.pipe(
      opLevels,
      distinctUntilChanged()
    );

    // the subjects
    const modeSubject = new BehaviorSubject<string>(DEFAULT_LAYOUT_MODE);

    // define the setters
    const onLayoutMode = modeSubject.pipe(
      opBoxLayoutMode,
      distinctUntilChanged()
    );

    const sitePageSubject = createSingleSubject<SitePage>();

    // sequence of site pages, make sure that they really differ
    const onSitePage = sitePageSubject.pipe(
      pageDistinctUntilChanged(),
      opShareLast
    );

    // build the chain
    const onRenderingContext: Observable<RenderingContext> = combineLatest(
      onSitePage,
      onLevels
    ).pipe(
      // fetch the context from our service
      switchMap<[SitePage, number], RenderingContext>(([sitePage, levels]) =>
        aWchService.getRenderingContextByPage(sitePage, levels)
      ),
      // cache the last value
      opShareLast
    );

    /**
     * Combines the latest changes
     */
    const onComponentState = (this.onState = combineLatest(
      onRenderingContext,
      onLayoutMode
    ));

    // define the setters
    defineProperties(that, {
      sitePage: createSetter(sitePageSubject, that),
      [KEY_LEVELS]: createSetter(levelsSubject, that),
      [KEY_LAYOUT_MODE]: createSetter(modeSubject, that)
    });

    // define the events
    this.onRenderingContext = onRenderingContext.pipe(onDestroy());
    this.onLayoutMode = onLayoutMode.pipe(onDestroy());
  }

  // just dispatch, otherwise the method will not be recognized
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
